// Workspace-specific API functions for settings, members, permissions, etc.
// This file is separate from features/workspaces/api.ts which handles basic CRUD
import { api, request } from '@/lib/api';

// LINT-DEBT (pre-existing, left as-is): import order; reordering changes module load order.
// eslint-disable-next-line import/order
import type { WorkspaceComplexityMode } from './types';
import type { OrgRole } from '@/types/organization';

export type WorkspaceApiData = {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  isPrivate?: boolean;
  ownerId?: string;
  owner?: {
    id?: string;
    name?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  homeNotes?: string;
};

// Alias for backwards compatibility
type Workspace = WorkspaceApiData;

export type WorkspaceMember = {
  id: string;
  userId?: string;
  email?: string;
  name?: string;
  role?: string;
  /** ONE-TRUTH-BE-1 (#1660): user_organizations.role for this member; null when there is no org row. */
  orgRole?: OrgRole | null;
  status?: 'active' | 'suspended';
  user?: {
    id?: string;
    email?: string;
    role?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  };
};

// Alias for backwards compatibility
type Member = WorkspaceMember;

type Permissions = {
  capabilities?: Record<string, Record<string, boolean>>;
};

function unwrapData<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'data' in (payload as Record<string, unknown>)
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

/** Stream B Build 2 — GET/PATCH /v1/workspaces/:id/complexity-mode (axios baseURL is `/api`). */
function complexityModeUrl(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/complexity-mode`;
}

export type WorkspaceComplexityModeDto = { mode: WorkspaceComplexityMode };

export type UpdateWorkspaceComplexityModeDto = {
  mode: WorkspaceComplexityMode;
  updatedAt: string;
  updatedBy?: string;
};

export async function getWorkspaceComplexityMode(
  workspaceId: string,
): Promise<WorkspaceComplexityModeDto> {
  const payload = await api.get<unknown>(complexityModeUrl(workspaceId));
  const body = unwrapData<{ mode: WorkspaceComplexityMode } | null>(payload);
  if (!body?.mode) {
    throw new Error('Invalid complexity mode response');
  }
  return { mode: body.mode };
}

export async function updateWorkspaceComplexityMode(
  workspaceId: string,
  mode: WorkspaceComplexityMode,
): Promise<UpdateWorkspaceComplexityModeDto> {
  const payload = await api.patch<unknown>(complexityModeUrl(workspaceId), { mode });
  const body = unwrapData<UpdateWorkspaceComplexityModeDto | null>(payload);
  if (!body?.mode || !body.updatedAt) {
    throw new Error('Invalid complexity mode update response');
  }
  return body;
}

export async function getWorkspace(id: string): Promise<Workspace | null> {
  try {
    const payload = await api.get<Workspace | { data: Workspace | null } | null>(
      `/workspaces/${id}`,
    );
    const workspace = unwrapData<Workspace | null>(payload);
    return workspace ?? null;
  } catch (error: any) {
    // Re-throw 403 (access denied) so caller can handle it
    if (error?.response?.status === 403) {
      throw error;
    }
    // Return null for 404 or other errors
    return null;
  }
}

export async function updateWorkspace(id: string, body: Partial<Workspace>): Promise<Workspace> {
  return api.patch(`/workspaces/${id}`, body);
}

export async function getMembers(id: string): Promise<Member[]> {
  try {
    const response = await api.get(`/workspaces/${id}/members`);
    return Array.isArray(response) ? response : response.data || [];
  } catch (error) {
    console.error('Failed to fetch workspace members:', error);
    return [];
  }
}

// New member management functions per Phase 3
/**
 * The organization's active members (GET /organizations/users).
 *
 * `request.get` is the unwrapped-payload client: the response interceptor has
 * already stripped the `{ success, data: {...} }` envelope, so the payload is
 * `{ users, total }` itself. This function used to read `response.data.users`
 * off the already-unwrapped value — `undefined` for every real response — and
 * then fall through to `[]`, so the roster was always empty. It also caught
 * every error and returned `[]`, so a failed request looked like an
 * organization with no members.
 *
 * It now throws on failure. Every caller has its own try/catch (or
 * `Promise.allSettled`) and decides what a failed read means to its surface.
 */
export async function listOrgUsersPage(): Promise<{ users: any[]; total: number }> {
  const payload = await request.get<
    { users?: any[]; data?: any[]; total?: number } | any[]
  >('/organizations/users');
  let users: any[] | null = null;
  if (Array.isArray(payload)) users = payload;
  else if (payload && Array.isArray(payload.users)) users = payload.users;
  // Legacy double-wrapped shape the previous implementation accepted.
  else if (payload && Array.isArray(payload.data)) users = payload.data;
  if (!users) throw new Error('Unexpected /organizations/users response shape');
  // The endpoint returns one page (default 100). `total` is the organization's
  // real member count; never report fewer than the rows actually received.
  const total =
    payload && !Array.isArray(payload) && typeof payload.total === 'number'
      ? Math.max(payload.total, users.length)
      : users.length;
  return { users, total };
}

export async function listOrgUsers(): Promise<any[]> {
  return (await listOrgUsersPage()).users;
}

export async function listWorkspaceMembers(workspaceId: string): Promise<Member[]> {
  try {
    const payload = await api.get<Member[] | { data: Member[] }>(
      `/workspaces/${workspaceId}/members`,
    );
    const unwrapped = unwrapData<Member[] | { data: Member[] }>(payload);
    if (Array.isArray(unwrapped)) {
      return unwrapped;
    }
    const nested =
      unwrapped && typeof unwrapped === 'object'
        ? (unwrapped as { data?: Member[] }).data
        : undefined;
    return Array.isArray(nested) ? nested : [];
  } catch (error) {
    console.error('Failed to fetch workspace members:', error);
    return [];
  }
}

export async function addWorkspaceMember(
  workspaceId: string,
  userId: string,
  role: 'workspace_owner' | 'workspace_member' | 'workspace_viewer'
): Promise<any> {
  try {
    return api.post(`/workspaces/${workspaceId}/members`, { userId, role });
  } catch (error) {
    console.error('Failed to add workspace member:', error);
    throw error;
  }
}

export async function changeWorkspaceRole(
  workspaceId: string,
  userId: string,
  role: 'workspace_owner' | 'workspace_member' | 'workspace_viewer'
): Promise<any> {
  try {
    return api.patch(`/workspaces/${workspaceId}/members/${userId}`, { role });
  } catch (error) {
    console.error('Failed to change workspace role:', error);
    throw error;
  }
}

export async function removeWorkspaceMember(workspaceId: string, userId: string): Promise<any> {
  try {
    return api.delete(`/workspaces/${workspaceId}/members/${userId}`);
  } catch (error) {
    console.error('Failed to remove workspace member:', error);
    throw error;
  }
}

/**
 * PROMPT 8: Suspend workspace member
 */
export async function suspendWorkspaceMember(
  workspaceId: string,
  memberId: string
): Promise<{ memberId: string; status: 'suspended' }> {
  try {
    const response = await api.patch<{ data: { memberId: string; status: 'suspended' } }>(
      `/workspaces/${workspaceId}/members/${memberId}/suspend`,
      {}
    );
    return response.data.data || { memberId, status: 'suspended' };
  } catch (error) {
    console.error('Failed to suspend member:', error);
    throw error;
  }
}

/**
 * PROMPT 8: Reinstate workspace member
 */
export async function reinstateWorkspaceMember(
  workspaceId: string,
  memberId: string
): Promise<{ memberId: string; status: 'active' }> {
  try {
    const response = await api.patch<{ data: { memberId: string; status: 'active' } }>(
      `/workspaces/${workspaceId}/members/${memberId}/reinstate`,
      {}
    );
    return response.data.data || { memberId, status: 'active' };
  } catch (error) {
    console.error('Failed to reinstate member:', error);
    throw error;
  }
}

export async function changeWorkspaceOwner(workspaceId: string, newOwnerId: string): Promise<any> {
  try {
    return api.post(`/workspaces/${workspaceId}/change-owner`, { newOwnerId });
  } catch (error) {
    console.error('Failed to change workspace owner:', error);
    throw error;
  }
}

// Legacy function - kept for backwards compatibility
// LINT-DEBT (pre-existing, left as-is): public signature of a deprecated function; removing the params changes callers.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function inviteMember(id: string, body: { email: string }): Promise<any> {
  // This should not be used - external invites go through /admin/invite
  console.warn("inviteMember is deprecated. Use /admin/invite for external invites.");
  throw new Error("External invites must go through /admin/invite. Use addWorkspaceMember for existing users.");
}

// Legacy function - kept for backwards compatibility
export async function removeMember(id: string, userId: string): Promise<any> {
  return removeWorkspaceMember(id, userId);
}

export async function getPermissions(id: string): Promise<Permissions> {
  try {
    return api.get(`/workspaces/${id}/permissions`);
  } catch (error) {
    console.error('Failed to fetch workspace permissions:', error);
    // Return default permissions if endpoint doesn't exist
    return {
      capabilities: {
        invite: { owner: true, member: true, viewer: false },
        delete: { owner: true, member: false, viewer: false },
        manage_templates: { owner: true, member: true, viewer: false },
      }
    };
  }
}

export async function updatePermissions(id: string, body: Permissions): Promise<Permissions> {
  try {
    return api.patch(`/workspaces/${id}/permissions`, body);
  } catch (error) {
    console.error('Failed to update permissions:', error);
    throw error;
  }
}

export async function listProjects(id: string): Promise<any[]> {
  try {
    const payload = await api.get<
      any[] | { projects?: any[] } | { data?: { projects?: any[] } }
    >(`/projects?workspaceId=${id}`);
    const unwrapped = unwrapData<
      any[] | { projects?: any[] } | { data?: { projects?: any[] } }
    >(payload);
    if (Array.isArray(unwrapped)) {
      return unwrapped;
    }
    if (unwrapped && typeof unwrapped === 'object') {
      const directProjects = (unwrapped as { projects?: any[] }).projects;
      if (Array.isArray(directProjects)) {
        return directProjects;
      }
      const nestedProjects = (unwrapped as { data?: { projects?: any[] } }).data
        ?.projects;
      if (Array.isArray(nestedProjects)) {
        return nestedProjects;
      }
    }
    return [];
  } catch (error) {
    console.error('Failed to fetch workspace projects:', error);
    return [];
  }
}

/**
 * Tasks due this week in the active workspace.
 * Uses GET /api/work/tasks with server-side dueFrom/dueTo. Requires x-workspace-id.
 */
// LINT-DEBT (pre-existing, left as-is): public signature; removing the param changes callers.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function listTasksDueThisWeek(_workspaceId: string): Promise<any[]> {
  try {
    const { listTasks } = await import('@/features/work-management/workTasks.api');
    const now = new Date();
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const dueFrom = now.toISOString().slice(0, 10);
    const dueTo = weekEnd.toISOString().slice(0, 10);
    const result = await listTasks({
      dueFrom,
      dueTo,
      limit: 200,
      sortBy: 'dueDate',
      sortDir: 'asc',
    });
    return result.items;
  } catch (error: any) {
    if (error?.response?.status === 404 || (error as any)?.code === 'WORKSPACE_REQUIRED') {
      return [];
    }
    console.error('Failed to fetch tasks due this week:', error);
    return [];
  }
}

export async function listRecentUpdates(id: string): Promise<any[]> {
  try {
    const updates = await api.get(`/api/activity?workspaceId=${id}&limit=10`);
    return Array.isArray(updates) ? updates : (updates.data || []);
  } catch (error: any) {
    // Phase 6: Gracefully handle 404s for empty workspaces (endpoint may not exist yet)
    if (error?.response?.status === 404) {
      return [];
    }
    console.error('Failed to fetch recent updates:', error);
    return [];
  }
}
